import { SERVICE_START_FALLBACK, loadServiceStartRule, serviceStartParticipant, serviceStartConfirmation } from '../utils/serviceStart';
import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { IoCheckmarkCircle, IoLockClosed, IoCloseCircle, IoAdd, IoRemove } from 'react-icons/io5';
import seedrandom from 'seedrandom';
import PaymentForm from './PaymentForm';
import { useExperimentWithExposureLoggingDisabled, Statsig } from 'statsig-react';
import { fetchQuote, submitQuote, submitHowHeard, fetchSpecialtyExtras, reportError } from '../Api/QuoteApi';
import { trackEvent, EVENTS, EXPERIMENTS, startFormTimer, getFormDuration, isSubmitPollOnTimeoutEnabled, isSubmitFastProvisioningEnabled, isSubmitTargetedLookupEnabled, isQuoteRevisionSigningEnabled, isQuoteRepairEnabled, getStatsigStableId } from '../utils/statsig';
import { WATER_SETUP_OPTIONS } from '../_lib/water-setup';
import { trackSubmitTelemetry } from '../utils/submitTelemetry';
import { normalizeServiceAddress } from '../utils/address';
import { resolveSpecialtyExtras, overriddenExtraPrice, quoteHasStandaloneSpa } from '../_lib/specialty-extras';
import { getPromo } from '../_lib/promos';
import { getMonthlyAgreementCards, normalizeVisitsPerWeek } from '../_lib/agreement-clauses';
import { seasonalSummaryLines, withActiveSeason } from '../_lib/seasonal-pricing';
import '../styles/quote-page.css';

// Submit error codes for which the customer fixes the problem themselves
// by re-entering / using a different card. Render the backend message
// verbatim (it already says what to do) and don't fire the client-side
// ops alert (declines are normal at scale, abandoners are tracked in
// Sell). CARD_DECLINED comes from the $0-auth path (submit.js:2189);
// CARD_REJECTED comes from the QBO store-on-file PMT-4xxx path
// (submit.js:2250). The backend skips its own notifyOps for both,
// relying on this set to keep the client from re-firing one.
const CUSTOMER_ACTIONABLE_CARD_CODES = new Set(['CARD_DECLINED', 'CARD_REJECTED']);

const generateNeighbourCount = (zipCode, min, max) => {
  const rng = seedrandom(zipCode);
  return Math.floor(rng() * (max - min + 1)) + min;
};

// Parse "<street>, <city>, <state> <zip>" into structured fields.
// Falls back to mostly-empty values when the address can't be parsed.
// Sell stores contact.address.state as either a 2-letter code ("CA")
// or a full state name ("California"), depending on which intake path
// created the contact. The original parser only matched 2-letter codes,
// which silently dropped state + zip when the input was "California
// 91942" -- that empty state then failed PaymentForm's billingAddress
// validation on a hidden field, with no visible error, and the submit
// click did nothing. This map normalizes either form to the 2-letter
// code QBO Payments tokenize requires for address.region.
const STATE_NAME_TO_ABBREV = {
  'alabama':'AL','alaska':'AK','arizona':'AZ','arkansas':'AR','california':'CA',
  'colorado':'CO','connecticut':'CT','delaware':'DE','district of columbia':'DC',
  'florida':'FL','georgia':'GA','hawaii':'HI','idaho':'ID','illinois':'IL',
  'indiana':'IN','iowa':'IA','kansas':'KS','kentucky':'KY','louisiana':'LA',
  'maine':'ME','maryland':'MD','massachusetts':'MA','michigan':'MI','minnesota':'MN',
  'mississippi':'MS','missouri':'MO','montana':'MT','nebraska':'NE','nevada':'NV',
  'new hampshire':'NH','new jersey':'NJ','new mexico':'NM','new york':'NY',
  'north carolina':'NC','north dakota':'ND','ohio':'OH','oklahoma':'OK','oregon':'OR',
  'pennsylvania':'PA','rhode island':'RI','south carolina':'SC','south dakota':'SD',
  'tennessee':'TN','texas':'TX','utah':'UT','vermont':'VT','virginia':'VA',
  'washington':'WA','west virginia':'WV','wisconsin':'WI','wyoming':'WY',
};

const parseAddress = (raw) => {
  if (!raw) return { street: '', city: '', state: '', zip: '' };
  const parts = raw.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { street: '', city: '', state: '', zip: '' };

  // Helpers shared across shape branches.
  const normalizeStateToken = (tok) => {
    const t = (tok || '').trim();
    if (!t) return '';
    if (t.length === 2) return t.toUpperCase();
    return STATE_NAME_TO_ABBREV[t.toLowerCase()] || t;
  };
  const zipOnly = (tok) => {
    const m = (tok || '').trim().match(/^(\d{5})(?:-\d{4})?$/);
    return m ? m[1] : '';
  };
  const stateZip = (tok) => {
    const m = (tok || '').match(/^(.+?)\s+(\d{5})/);
    if (!m) return null;
    return { state: normalizeStateToken(m[1]), zip: m[2] };
  };

  // Branch on the comma-part count. Sell publisher (post-2026-05-26)
  // emits cleaned 3-part strings, but legacy KV entries (30-day TTL)
  // and the fallback path here have to tolerate 2- and 4-part shapes
  // too. When the structured serviceAddressFields is present in the
  // KV record, none of this runs (QuotePage prefers it; this is only
  // the legacy fallback).
  if (parts.length >= 3) {
    // "street, city, state zip" (canonical) or "street, city, state, zip"
    const tail = parts[parts.length - 1];
    const second = parts[parts.length - 2];
    const sz = stateZip(tail);
    const onlyZip = zipOnly(tail);
    if (sz) {
      const city = parts.slice(1, parts.length - 1).join(', ');
      return { street: parts[0], city, state: sz.state, zip: sz.zip };
    }
    if (onlyZip && parts.length >= 4) {
      // "street, city, state, zip"
      return { street: parts[0], city: parts.slice(1, parts.length - 2).join(', '), state: normalizeStateToken(second), zip: onlyZip };
    }
    if (onlyZip) {
      // "street, city, zip"
      return { street: parts[0], city: parts.slice(1, parts.length - 1).join(', '), state: '', zip: onlyZip };
    }
    // Tail is neither state-zip nor zip; treat as city overflow.
    return { street: parts[0], city: parts.slice(1).join(', '), state: '', zip: '' };
  }

  if (parts.length === 2) {
    // "street, state zip" or "street, zip" or "street, state"
    const tail = parts[1];
    const sz = stateZip(tail);
    if (sz) return { street: parts[0], city: '', state: sz.state, zip: sz.zip };
    const onlyZip = zipOnly(tail);
    if (onlyZip) return { street: parts[0], city: '', state: '', zip: onlyZip };
    const maybeState = normalizeStateToken(tail);
    if (maybeState && maybeState.length === 2) {
      return { street: parts[0], city: '', state: maybeState, zip: '' };
    }
    // Tail does not look like state/zip; treat as city.
    return { street: parts[0], city: tail, state: '', zip: '' };
  }

  // parts.length === 1
  return { street: parts[0], city: '', state: '', zip: '' };
};

// Render a money amount as the dollar portion (no $ sign). Whole
// numbers stay clean (185 = "185"); fractional values always show
// two decimals (557.5 = "557.50") so a list like $185 + $557.50 +
// $557.50 visually adds up to $1300 instead of "$557.5".
const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v === Math.trunc(v) ? String(Math.trunc(v)) : v.toFixed(2);
};

// Scroll reveal hook using IntersectionObserver
const useReveal = (threshold = 0.15, onVisible) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          if (onVisible) onVisible();
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, onVisible]);

  return [ref, visible];
};

// Reusable reveal wrapper
const Reveal = ({ children, className = '', type = 'up', section }) => {
  const onVisible = useCallback(() => {
    if (section) {
      trackEvent(EVENTS.QUOTE_SECTION_VIEWED, { section });
    }
  }, [section]);
  const [ref, visible] = useReveal(0.15, onVisible);
  const typeClass = type === 'left' ? 'qp-reveal-left'
    : type === 'right' ? 'qp-reveal-right'
    : type === 'scale' ? 'qp-reveal-scale'
    : type === 'stagger' ? 'qp-stagger'
    : 'qp-reveal';

  return (
    <div ref={ref} className={`${typeClass} ${visible ? 'qp-visible' : ''} ${className}`}>
      {children}
    </div>
  );
};

// Checkmark SVG for pricing tags
const CheckSvg = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
  </svg>
);

// Star SVG for trust bar
const StarSvg = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
  </svg>
);

// Demo data for local testing
const DEMO_QUOTES = {
  demo: {
    dealId: 'demo',
    contactName: 'John Smith',
    contactEmail: 'john@example.com',
    contactPhone: '+16195551234',
    address: '4521 Caminito Estrellado, San Diego, CA 92107',
    zipCode: '92107',
    brand: 'Cabana',
    region: 'CA1',
    hasMonthlyService: true,
    monthlyPrice: 185,
    weeklyPrice: 46,
    hasSpa: true,
    hasSaltSystem: false,
    serviceTier: 'full',
    modifiers: ['spa'],
    serviceLineItems: [
      { name: 'Weekly Pool Service', monthlyPrice: 180 },
      { name: 'Spa Service', monthlyPrice: 5 },
    ],
    oneTimeServices: [
      { name: 'Green-to-Clean', price: 350 },
    ],
    status: 'pending',
  },
  'demo-seasonal-alternate': {
    dealId: 'demo-seasonal-alternate', contactName: 'Seasonal Service Preview',
    contactEmail: 'preview@example.com', address: 'Example property, Fallbrook, CA 92028',
    zipCode: '92028', brand: 'Cabana', region: 'CA1', hasMonthlyService: true,
    monthlyPrice: 88, weeklyPrice: 22, hasSpa: true, hasSaltSystem: true,
    serviceTier: 'full', paymentTerms: 'check', oneTimeServices: [],
    seasonalPricing: {
      summer: { monthlyPrice: 176, visitsPerWeek: 1, startMonth: 5 },
      winter: { monthlyPrice: 88, visitsPerWeek: 0.5, startMonth: 11 },
      startWindow: 'mid-November 2026', startMonth: '2026-11',
    },
    status: 'pending',
  },
  'demo-seasonal': {
    dealId: 'demo-seasonal',
    contactName: 'Maria Lopez',
    contactEmail: 'maria@example.com',
    contactPhone: '+13105551234',
    address: '1200 Example Ave, Torrance, CA 90504',
    zipCode: '90504',
    brand: 'Cabana',
    region: 'CA2',
    hasMonthlyService: true,
    monthlyPrice: 390,
    weeklyPrice: 98,
    hasSpa: true,
    hasSaltSystem: false,
    serviceTier: 'full',
    modifiers: ['spa'],
    serviceLineItems: [
      { name: 'CA2 Pool Cleaning (Weekly)', monthlyPrice: 340 },
      { name: 'CA2 + Spa (Weekly)', monthlyPrice: 20 },
      { name: 'CA2 + Trees (Weekly)', monthlyPrice: 30 },
    ],
    oneTimeServices: [],
    seasonalPricing: {
      summer: { monthlyPrice: 390, visitsPerWeek: 2, startMonth: 5 },
      winter: { monthlyPrice: 215, visitsPerWeek: 1, startMonth: 10 },
    },
    status: 'pending',
  },
  'demo-onetime': {
    dealId: 'demo-onetime',
    contactName: 'Sarah Johnson',
    contactEmail: 'sarah@example.com',
    contactPhone: '+18185559876',
    address: '789 Sunset Blvd, Los Angeles, CA 90028',
    zipCode: '90028',
    brand: 'Cabana',
    region: 'CA2',
    hasMonthlyService: false,
    monthlyPrice: 0,
    weeklyPrice: 0,
    hasSpa: false,
    hasSaltSystem: false,
    serviceTier: 'full',
    modifiers: [],
    serviceLineItems: [],
    oneTimeServices: [
      { name: 'Green-to-Clean', price: 450 },
      { name: 'Filter Deep Clean', price: 150 },
    ],
    status: 'pending',
  },
  'demo-salt': {
    dealId: 'demo-salt',
    contactName: 'Mike Chen',
    contactEmail: 'mike@example.com',
    contactPhone: '+19165557890',
    address: '1200 Capitol Mall, Sacramento, CA 95814',
    zipCode: '95814',
    brand: 'Cabana',
    region: 'CA3',
    hasMonthlyService: true,
    monthlyPrice: 210,
    weeklyPrice: 52,
    hasSpa: false,
    hasSaltSystem: true,
    serviceTier: 'chem_only',
    modifiers: ['large-pool'],
    serviceLineItems: [
      { name: 'Weekly Pool Service (Large)', monthlyPrice: 210 },
    ],
    oneTimeServices: [],
    status: 'pending',
  },
};

const QuotePage = ({ config }) => {
  const { dealId } = useParams();
  const [revisionCohort] = useState(() => isQuoteRevisionSigningEnabled());
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [startParticipant] = useState(() => serviceStartParticipant(document.cookie, getStatsigStableId(), config.website));
  const [startEnabled, setStartEnabled] = useState(startParticipant);
  const [startRule, setStartRule] = useState(null);
  const [startPromise, setStartPromise] = useState(null);
  const { config: startExperiment } = useExperimentWithExposureLoggingDisabled('cabana_service_start_v1');
  useEffect(() => {
    if (!startParticipant) return;
    let cancelled = false;
    loadServiceStartRule().then(rule => {
      if (cancelled) return;
      if (rule === undefined) setStartEnabled(false);
      else setStartRule(rule);
    });
    return () => { cancelled = true; };
  }, [startParticipant]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [reviewRequired, setReviewRequired] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(false);
  const [confirmHowHeard, setConfirmHowHeard] = useState('');
  const [howHeardOtherText, setHowHeardOtherText] = useState('');
  const [howHeardSaved, setHowHeardSaved] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [agreementError, setAgreementError] = useState(false);
  const [openAgreementCards, setOpenAgreementCards] = useState({});
  const [specialtyExtras, setSpecialtyExtras] = useState(null);
  // A hero image that fails to load leaves a broken-image box at the top of
  // the customer's proposal. Drop the frame instead; the gradient hero still
  // reads as designed without it.
  const [heroImageFailed, setHeroImageFailed] = useState(false);
  const [accessInfo, setAccessInfo] = useState({
    gateCode: '',
    needsKey: '',
    notes: '',
  });
  const [waterSetup, setWaterSetup] = useState('');
  const [waterSetupError, setWaterSetupError] = useState(false);

  const paymentFormRef = useRef(null);
  const submitSectionRef = useRef(null);
  const pricingCardRef = useRef(null);

  // Derive hasMonthly / monthlyPrice / weeklyPrice from the line items
  // when the headline fields are missing. residential-quote-send's
  // older heuristic only checked the line item's `description` field
  // for "weekly"/"monthly" hints, so any deal whose Sell line uses the
  // word in its NAME (e.g. "CA2 Pool Cleaning (Weekly)") got published
  // with hasMonthlyService=false + monthlyPrice=0 and the page would
  // render with no pricing/payment/agreement sections at all. This
  // backfill rescues already-stored bad records.
  const effective = useMemo(() => {
    if (!quote) return { hasMonthly: false, monthlyPrice: 0, weeklyPrice: 0, hasOneTime: false };
    const sumLines = (quote.serviceLineItems || []).reduce(
      (s, li) => s + Number(li.monthlyPrice || 0),
      0,
    );
    const rawMonthly = Number(quote.monthlyPrice) || sumLines;
    const rawWeekly = Number(quote.weeklyPrice) || (rawMonthly ? Math.round(rawMonthly / 4) : 0);
    const rawHasMonthly = !!quote.hasMonthlyService || rawMonthly > 0;
    const hasOneTime = Array.isArray(quote.oneTimeServices) && quote.oneTimeServices.length > 0;
    return { hasMonthly: rawHasMonthly, monthlyPrice: rawMonthly, weeklyPrice: rawWeekly, hasOneTime };
  }, [quote]);
  const effectiveHasMonthly = effective.hasMonthly;
  const showStartPromise = startEnabled && startExperiment.get('arm', 'control') === 'treatment' &&
    effectiveHasMonthly && quote?.brand === 'Cabana';
  const effectiveMonthlyPrice = effective.monthlyPrice;
  const effectiveWeeklyPrice = effective.weeklyPrice;
  // One-time signings always ask for the pool and spa setup (the onetime_water_question test is retired).
  const askWaterSetup = !!quote && !effectiveHasMonthly && effective.hasOneTime && quote.skipWaterSetup !== true;
  const isSpaOnly = (quote && quote.serviceTier) === 'spa_only';
  const visitsPerWeek = normalizeVisitsPerWeek(quote?.visitsPerWeek);
  const isTwiceWeekly = visitsPerWeek === 2;
  const isAlternateWeeks = visitsPerWeek === 0.5;
  const perVisitSeasonal = quote?.seasonalPricing && [quote.seasonalPricing.summer, quote.seasonalPricing.winter].some(s => s.visitsPerWeek === 0.5);
  const cadenceLabel = isAlternateWeeks ? 'Every-other-week' : isTwiceWeekly ? 'Twice-weekly' : 'Weekly';

  // CTA copy A/B (item 7 from #443 third-review). Variant copy comes
  // from the experiment config; defaults are the historical control
  // copy so the page renders correctly even when the experiment isn't
  // wired up in Statsig yet. Exposure is logged manually once we know
  // the customer actually reached the page (not on every error/404
  // bounce), to avoid diluting the experiment.
  const { config: ctaExperiment } = useExperimentWithExposureLoggingDisabled(EXPERIMENTS.QUOTE_CTA_COPY);
  const ctaCopyMonthly = ctaExperiment.get('cta_monthly', 'Start My Service');
  const ctaCopyOnetime = ctaExperiment.get('cta_onetime', 'Confirm Services');
  const ctaVariantName = ctaExperiment.getGroupName?.() || 'control';
  const ctaExposureLogged = useRef(false);

  // The global window.onerror / unhandledrejection capture that used
  // to live here paged ops on every cross-origin script error (the
  // browser's CORS-stripped "Script error. @ :0:0" sentinel) and on
  // every ad-blocker-dropped marketing pixel. A 30-day audit found
  // 12 of 20 alerts came from that path, all unactionable, and the
  // one customer whose error triggered the latest review (deal
  // 198900597) signed successfully the same minute. Per-stage actionable
  // capture lives in the relevant call sites instead: handleSubmit
  // (submit failure), PaymentForm tokenize/validate (payment failure),
  // and ErrorBoundary (render crash). Vendor RUM (Sentry/Bugsnag/Datadog)
  // is the planned home for non-actionable browser telemetry.

  // Warm the submit lambda + QBO token cache the moment the signing page
  // mounts, so the customer's eventual submit skips the cold start and the
  // token refresh. Fire-and-forget: a failed warm-up costs nothing (the
  // submit still works, just colder). The 204 body is never read.
  useEffect(() => {
    try {
      // Send the whole `<dealId>-<accessToken>` URL segment: the server
      // validates the token before warming, so a guessed deal id cannot
      // trigger an Intuit token refresh.
      const dealParam = (window.location.pathname.split('/q/')[1] || '').split('/')[0];
      fetch(`${process.env.REACT_APP_QUOTE_API_URL || ''}/api/quote/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warm: true, dealId: dealParam }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore: warm-up is purely opportunistic.
    }
  }, []);

  useEffect(() => {
    const loadQuote = async () => {
      if (DEMO_QUOTES[dealId]) {
        setQuote(withActiveSeason(DEMO_QUOTES[dealId]));
        setLoading(false);
        trackEvent(EVENTS.QUOTE_PAGE_VIEWED, {
          deal_id: dealId,
          has_monthly: DEMO_QUOTES[dealId].hasMonthlyService,
          has_spa: DEMO_QUOTES[dealId].hasSpa,
          region: DEMO_QUOTES[dealId].region,
        });
        return;
      }

      try {
        const data = await fetchQuote(dealId, { context: 'quote', revisionCheck: revisionCohort });
        // Never substitute weekly terms when the negotiated cadence is gated off.
        if (data.seasonalPricing && [data.seasonalPricing.summer, data.seasonalPricing.winter].some(s => s?.visitsPerWeek === 0.5)
            && !isQuoteRepairEnabled('seasonal_every_other_week')) {
          setError('load_failed');
          return;
        }
        // Use the starting season until service begins.
        setQuote(withActiveSeason(data));
        trackEvent(EVENTS.QUOTE_PAGE_VIEWED, {
          deal_id: dealId,
          revision_cohort: revisionCohort ? 'treatment' : 'control',
          has_monthly: data.hasMonthlyService,
          has_spa: data.hasSpa,
          region: data.region,
        });
      } catch (err) {
        // 404 from /api/quote/:dealId means the Vercel KV entry is
        // gone. KV entries TTL at 30 days; cabana-quote-expirer then
        // flips the Sell deal to Lost at 31 days. So any 404 against
        // a token-validated URL the customer received in email or
        // SMS is overwhelmingly "expired", not "wrong link". Surface
        // it as expired so the customer copy matches reality.
        setError(err.response?.data?.code || (err.response?.status === 404 ? 'expired' : 'load_failed'));
      } finally {
        setLoading(false);
      }
    };

    loadQuote();
  }, [dealId, revisionCohort]);

  // CTA copy A/B exposure: log once, only for customers who actually
  // reached a renderable quote (skips 404s and KV-misses so they don't
  // dilute the variant population).
  useEffect(() => {
    if (!quote || ctaExposureLogged.current) return;
    ctaExposureLogged.current = true;
    try {
      Statsig.manuallyLogExperimentExposure(EXPERIMENTS.QUOTE_CTA_COPY);
    } catch { /* swallow: Statsig may not be initialized in dev */ }
    trackEvent(EVENTS.QUOTE_CTA_VARIANT_RENDERED, {
      deal_id: dealId,
      variant: ctaVariantName,
      cta_monthly: ctaCopyMonthly,
      cta_onetime: ctaCopyOnetime,
      has_monthly: effectiveHasMonthly,
    });
  }, [quote, dealId, ctaVariantName, ctaCopyMonthly, ctaCopyOnetime]);

  // Mobile-abandon signal: fire QUOTE_PAGE_LEFT once when the tab is
  // hidden or the page is being unloaded. Tracks the customer's stage
  // at exit (signed / ready / consented / payment / browsing) so the
  // funnel chart can show where mobile users drop off.
  const pageLeftFiredRef = useRef(false);
  useEffect(() => {
    if (!quote) return;
    const fire = () => {
      if (pageLeftFiredRef.current || submitted) return;
      pageLeftFiredRef.current = true;
      const stage = submitting
        ? 'submitting'
        : agreementChecked
          ? 'consented_unsubmitted'
          : openAgreementCards && Object.values(openAgreementCards).some(Boolean)
            ? 'browsing_agreement'
            : 'browsing';
      trackEvent(EVENTS.QUOTE_PAGE_LEFT, {
        deal_id: dealId,
        stage,
        has_monthly: effectiveHasMonthly,
        consent_checked: agreementChecked,
      });
    };
    const onVisibility = () => { if (document.visibilityState === 'hidden') fire(); };
    window.addEventListener('pagehide', fire);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', fire);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [quote, dealId, submitted, submitting, agreementChecked, openAgreementCards]);

  // Fetch the regional specialty-treatment list once we know the customer's zip.
  // Failure is non-blocking: we fall back to the static brand.js addons.
  useEffect(() => {
    if (!quote) return;
    const zip = quote.zipCode;
    if (!zip) return;
    let cancelled = false;
    fetchSpecialtyExtras({ brand: config.pricingBrand, zipCode: zip, spaConstruction: quote.spaConstruction })
      .then((data) => {
        if (!cancelled && Array.isArray(data?.extras)) {
          setSpecialtyExtras(data.extras);
        }
      })
      .catch(() => { /* fall back to static config */ });
    return () => { cancelled = true; };
  }, [quote]);

  // Scroll tracking + sticky CTA visibility
  useEffect(() => {
    if (!quote) return;
    startFormTimer();

    const depthMarkers = { 25: false, 50: false, 75: false, 100: false };
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;
      const pct = Math.round((scrollTop / docHeight) * 100);
      for (const mark of [25, 50, 75, 100]) {
        if (pct >= mark && !depthMarkers[mark]) {
          depthMarkers[mark] = true;
          trackEvent(EVENTS.QUOTE_SCROLL_DEPTH, { depth: mark, deal_id: dealId });
        }
      }

      // Sticky CTA: show after scrolling past pricing card; hide once user reaches submit section
      const pricingEl = pricingCardRef.current;
      const submitEl = submitSectionRef.current;
      if (pricingEl && submitEl) {
        const pricingBottom = pricingEl.getBoundingClientRect().bottom;
        const submitTop = submitEl.getBoundingClientRect().top;
        const viewportH = window.innerHeight;
        setStickyVisible(pricingBottom < 0 && submitTop > viewportH);
      }
    };

    const handleAbandon = () => {
      trackEvent(EVENTS.STEP_ABANDONED, {
        step_number: 'quote_page',
        deal_id: dealId,
        duration: getFormDuration(),
        has_monthly: effectiveHasMonthly,
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('beforeunload', handleAbandon);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('beforeunload', handleAbandon);
    };
  }, [quote, dealId]);

  const handleAccessChange = useCallback((e) => {
    const { name, value } = e.target;
    setAccessInfo((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async () => {
    if (submitting || submitted) return;

    if (!agreementChecked) {
      setAgreementError(true);
      const consentEl = document.getElementById('qp-consent-checkbox');
      if (consentEl) consentEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    if (askWaterSetup && !waterSetup) {
      setWaterSetupError(true);
      const setupEl = document.getElementById('qp-water-setup');
      if (setupEl) setupEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    let paymentData = null;
    const hasOneTimeNow = quote.oneTimeServices && quote.oneTimeServices.length > 0;
    if ((effectiveHasMonthly || hasOneTimeNow) && paymentFormRef.current) {
      paymentData = await paymentFormRef.current.getData();
      if (!paymentData) return;
    }

    setSubmitError(null);
    setReviewRequired(false);
    setSubmitting(true);
    trackEvent(EVENTS.QUOTE_SUBMIT_ATTEMPTED, {
      revision_cohort: revisionCohort ? 'treatment' : 'control',
      deal_id: dealId,
      payment_method: paymentData?.paymentMethod || 'none',
      has_monthly: effectiveHasMonthly,
      duration: getFormDuration(),
    });

    // Requested treatment (client gate value). The server applies it only
    // when its own kill switch is on, so the authoritative applied value
    // comes back in the response timing; both are recorded below.
    const fastRequested = isSubmitFastProvisioningEnabled();
    // Targeted customer lookup. Gated per the standing submit-path rule and
    // only honored when the server's own env switch is on; the applied
    // value comes back as timing.lookupMode.
    const targetedLookupRequested = isSubmitTargetedLookupEnabled();
    // A revision bundle is an integrity boundary, not experimental UX.  Once
    // the server has returned revision metadata it must be echoed on every
    // submit; Statsig can measure the rollout but may not remove the stale
    // content check for one cohort.
    const revisionSigning = !!quote.quoteRevisionId && !!quote.contentDigest && !!quote.templateVersion;
    const submitStartMs = Date.now();
    try {
      const submitResult = await submitQuote(dealId, {
        paymentInfo: paymentData,
        accessInfo,
        ...(askWaterSetup ? { waterSetup } : {}),
        contactName: quote.contactName,
        contactEmail: quote.contactEmail,
        renderedExtras: resolveSpecialtyExtras(specialtyExtras, quote),
        // Fast provisioning: ask the server to defer the card-flag PATCH so
        // the customer waits only on the card store and $0 decline check.
        // Gated (default off) per the submit-path Statsig rule and only
        // honored when the server kill switch is on; stamped into the body.
        fastProvisioning: fastRequested,
        // Targeted customer lookup: ask the server to resolve a returning
        // customer with a targeted query plus a live ownership confirm
        // instead of scanning the whole customer book. Uncertain shapes
        // still scan, so this changes latency, not matching semantics.
        targetedLookup: targetedLookupRequested,
        ...(revisionSigning ? {
          revisionCheck: true,
          quoteRevisionId: quote.quoteRevisionId,
          contentDigest: quote.contentDigest,
          templateVersion: quote.templateVersion,
          consentAccepted: true,
        } : {}),
      }, {
        // On a submit timeout, poll for the signed state instead of
        // dumping the customer on an error screen while the server is
        // still finishing an already-succeeding signing. Gated so it
        // rolls out behind the standing submit-path Statsig rule.
        pollForSignedOnTimeout: isSubmitPollOnTimeoutEnabled(),
      });
      setSubmitted(true);
      if (showStartPromise) {
        setStartPromise({ dealId, text: serviceStartConfirmation(startRule, new Date(), true) });
      }
      trackEvent(EVENTS.QUOTE_SIGNED, {
        revision_cohort: revisionCohort ? 'treatment' : 'control',
        deal_id: dealId,
        payment_method: paymentData?.paymentMethod || 'none',
        has_monthly: effectiveHasMonthly,
        duration: getFormDuration(),
      });
      // Signing-latency instrumentation. duration_ms is the client-observed
      // round trip (matching the existing lead-form semantics); the server's
      // per-step timing rides under server_* keys, which land in extras_json.
      // page is a fixed label, never window.location.pathname: the signing
      // URL is /q/<dealId>-<accessToken> and the pathname would leak the
      // quote bearer token into telemetry. Fires on every signing success,
      // including the poll-recovery path (server timing is absent there).
      const timing = submitResult && submitResult.timing ? submitResult.timing : null;
      trackSubmitTelemetry('form_submit_success', {
        submit_kind: 'quote_signing',
        page: 'quote_signing',
        http_status: 200,
        duration_ms: Date.now() - submitStartMs,
        statsig_stable_id: getStatsigStableId(),
        payment_method: paymentData?.paymentMethod || 'none',
        treatment_requested: fastRequested,
        treatment_targeted_lookup: targetedLookupRequested,
        recovered_via_poll: submitResult?.recoveredViaPoll === true ? true : undefined,
        server_total_ms: timing ? timing.total : undefined,
        server_qbo_critical_ms: timing ? timing.qboCritical : undefined,
        server_nudge_ms: timing ? timing.nudge : undefined,
        server_fast_provisioning: timing ? timing.fastProvisioning : undefined,
        server_steps_json: timing && timing.steps ? JSON.stringify(timing.steps) : undefined,
        server_lookup_mode: timing ? timing.lookupMode : undefined,
        has_monthly: timing ? timing.hasMonthly : effectiveHasMonthly,
      });
    } catch (err) {
      // Surface the real backend error message so the customer (and
      // ops, when they call in) knows what's actually wrong. Falls
      // back to a generic prompt if no message is available.
      //
      // Defensive coercion: edge errors (Vercel function timeout
      // returns `{error: {code, message}}`, rate limits return a
      // similar shape) put a non-string under `data.error`. Without
      // the coercion below, a 504 rendered "Couldn't complete signup:
      // [object Object]" to the customer because the template literal
      // toString'd the whole object. Found in the wild during PR #46
      // verification on prod.
      const detailRaw = err?.response?.data?.detail
        || err?.response?.data?.error
        || err?.message
        || '';
      const detail = typeof detailRaw === 'string'
        ? detailRaw
        : (detailRaw?.message || 'request failed');
      const code = err?.response?.data?.code || null;
      if (['quote_changed', 'quote_review_required'].includes(code)) {
        setReviewRequired(true);
        setSubmitError('This quote changed while you were reviewing it. Reload it and review the current terms before signing.');
      }
      // Customer-actionable card errors (see CUSTOMER_ACTIONABLE_CARD_CODES
      // near the top of this file): the backend message already reads
      // "Your card was declined. Please try a different card..." or
      // "We couldn't verify that card. Please double-check..." so
      // wrapping it in "Couldn't complete signup ... Please try again"
      // double-emphasizes "try again" and adds a hotline that isn't
      // the right call-to-action (the customer doesn't need to call
      // us; they need a different card). Render the message verbatim.
      const message = ['quote_changed', 'quote_review_required'].includes(code)
        ? 'This quote changed while you were reviewing it. Reload it and review the current terms before signing.'
        : CUSTOMER_ACTIONABLE_CARD_CODES.has(code)
        ? detail
        : (detail
            ? `Couldn't complete signup: ${detail}. Please try again or call us at ${config.phone}.`
            : `Something went wrong. Please try again or call us at ${config.phone}.`);
      setSubmitError(message);
      // Scroll the customer to the error message so they actually see
      // it (the submit button often sits below the fold of a long
      // form). Do this on next paint so the error has rendered.
      requestAnimationFrame(() => {
        const errEl = document.querySelector('.qp-submit-error, [data-submit-error]');
        if (errEl) errEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
      trackEvent(EVENTS.QUOTE_SUBMIT_ERROR, {
        deal_id: dealId,
        error: err.message || 'unknown',
        code: code || 'unknown',
      });
      // Alert ops so we never silently lose a customer, EXCEPT:
      //   1. Customer-actionable card errors (CUSTOMER_ACTIONABLE_CARD_CODES:
      //      CARD_DECLINED from the $0-auth path, CARD_REJECTED from the
      //      QBO store-on-file PMT-4xxx path). The customer just retries
      //      with a different card; an ops ticket per attempt is noisy
      //      and un-actionable. Abandoners are still tracked in Sell.
      //   2. The backend response already set serverAlerted: true. The
      //      submit handler called notifyOps itself before returning
      //      the error (QBO_INTEGRATION_ERROR, PERMANENT confirmation
      //      email failure, etc.), so the client report would just
      //      duplicate the alert.
      const serverAlerted = err?.response?.data?.serverAlerted === true;
      if (!CUSTOMER_ACTIONABLE_CARD_CODES.has(code) && !serverAlerted) {
        reportError({
          dealId,
          stage: 'submitQuote',
          message: detail || err.message || 'unknown',
          contactEmail: quote.contactEmail,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleHowHeardChange = async (e) => {
    const value = e.target.value;
    setConfirmHowHeard(value);
    if (!value) return;
    // For "other", wait for the free-text submit before recording.
    if (value === 'other') return;
    try {
      await submitHowHeard(dealId, value);
      setHowHeardSaved(true);
      trackEvent(EVENTS.QUOTE_HOWHEARD_SUBMITTED, { deal_id: dealId, how_heard: value });
    } catch (err) {
      // Non-blocking; the deal is already signed.
    }
  };

  const submitHowHeardOther = async () => {
    const text = howHeardOtherText.trim();
    if (!text) return;
    try {
      await submitHowHeard(dealId, `other: ${text}`.slice(0, 200));
      setHowHeardSaved(true);
      trackEvent(EVENTS.QUOTE_HOWHEARD_SUBMITTED, { deal_id: dealId, how_heard: 'other_text' });
    } catch (err) {
      /* non-blocking */
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="qp-loading">
        <img src={config.logo} alt={config.name} className="qp-loading-logo" />
        <div className="qp-loading-spinner" />
        <p>Loading your quote...</p>
      </div>
    );
  }

  // Error / expired
  if (error) {
    const isExpired = error === 'expired' || error === 'quote_expired';
    const isVoid = error === 'quote_void' || error === 'quote_revoked';
    const heading = isVoid ? 'This quote is no longer available' : (isExpired ? 'This quote has expired' : 'Oops');
    const body = isVoid
      ? 'Please contact us so we can review the right next step with you.'
      : isExpired
      ? 'Cabana Pools quotes are valid for 30 days. Give us a call and we will put together a fresh quote for you.'
      : 'Unable to load quote. Please try again.';
    const homeUrl = `https://${config.website}/`;
    return (
      <div className="qp-error-page">
        <a href={homeUrl} aria-label={`${config.name} home`}>
          <img src={config.logo} alt={config.name} className="qp-loading-logo" />
        </a>
        <h2>{heading}</h2>
        <p>{body}</p>
        <p>Call us at <a href={`tel:${config.phone}`}>{config.phone}</a>{isExpired ? '.' : " and we'll get you sorted."}</p>
      </div>
    );
  }

  // Already signed / just submitted
  if (quote.status === 'signed' || submitted) {
    return (
      <div className="qp-confirmation">
        <img src={config.logo} alt={config.name} className="qp-loading-logo" />
        <div className="qp-confirmation-icon">
          <IoCheckmarkCircle size={64} color="#10B981" />
        </div>
        <h2>You're all set, {quote.contactName?.split(' ')[0]}!</h2>
        <p className="qp-confirmation-email">
          Your agreement has been recorded. We will send your confirmation to <strong>{quote.contactEmail}</strong>.
        </p>

        <div className="qp-confirmation-timeline">
          <h3>What Happens Next</h3>
          <div className="qp-confirmation-steps">
            <div className="qp-confirmation-step">
              <div className="qp-confirmation-step-num">1</div>
              <div>
                <strong>Scheduling (within 48 hours)</strong>
                <p>{effectiveHasMonthly
                  ? `We'll reach out to confirm your service ${isTwiceWeekly ? 'days' : 'day'} and introduce your technician.`
                  : "We'll reach out to confirm your service date."}</p>
              </div>
            </div>
            <div className="qp-confirmation-step">
              <div className="qp-confirmation-step-num">2</div>
              <div>
                <strong>{quote.seasonalPricing?.startWindow ? `First visit: ${quote.seasonalPricing.startWindow}` : showStartPromise ? (submitted && startPromise?.dealId === dealId ? startPromise.text : SERVICE_START_FALLBACK) : effectiveHasMonthly
                  ? 'First visit (usually within 5 business days)'
                  : 'Service date (usually within 5 business days)'}</strong>
                <p>{effectiveHasMonthly
                  ? 'Your technician performs a full service and sends you a photo report.'
                  : 'We confirm the exact date with you, then schedule the visit.'}</p>
              </div>
            </div>
            {effectiveHasMonthly ? (
              <div className="qp-confirmation-step">
                <div className="qp-confirmation-step-num">3</div>
                <div>
                  <strong>{cadenceLabel} service begins</strong>
                  <p>{isAlternateWeeks ? 'Every other week in winter, weekly in summer.' : `Same ${isTwiceWeekly ? 'days' : 'day'} each week.`} Same technician. You'll get photo reports after every visit.</p>
                </div>
              </div>
            ) : (
              <div className="qp-confirmation-step">
                <div className="qp-confirmation-step-num">3</div>
                <div>
                  <strong>Photo report</strong>
                  <p>Emailed after the visit so you can see the finished pool.</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {effectiveHasMonthly && (
          <div className="qp-confirmation-summary">
            <div className="qp-confirmation-summary-row">
              <span>Service</span>
              <span>{cadenceLabel} {isSpaOnly ? 'Spa' : 'Pool'} Care</span>
            </div>
            <div className="qp-confirmation-summary-row">
              <span>Monthly rate</span>
              <strong>${fmtMoney(effectiveMonthlyPrice)}/mo</strong>
            </div>
            <div className="qp-confirmation-summary-row">
              <span>Property</span>
              <span>{quote.address}</span>
            </div>
          </div>
        )}

        <div className="qp-confirmation-howheard">
          <label htmlFor="qp-confirm-howheard">How did you hear about us?</label>
          <select
            id="qp-confirm-howheard"
            value={confirmHowHeard}
            onChange={handleHowHeardChange}
            disabled={howHeardSaved}
          >
            <option value="">Select...</option>
            <option value="google_search">Google</option>
            <option value="referral">Friend, family, or neighbor referral</option>
            <option value="facebook_instagram">Facebook or Instagram</option>
            <option value="other_search">Bing, Yahoo, or other search engine</option>
            <option value="ai_assistant">ChatGPT or other AI assistant</option>
            <option value="nextdoor">Nextdoor</option>
            <option value="truck">Pool truck</option>
            <option value="equipment_sticker">Equipment sticker</option>
            <option value="yelp">Yelp</option>
            <option value="other">Other</option>
          </select>
          {confirmHowHeard === 'other' && !howHeardSaved && (
            <div className="qp-howheard-other">
              <input
                type="text"
                placeholder="Tell us where you heard about us"
                value={howHeardOtherText}
                onChange={(e) => setHowHeardOtherText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitHowHeardOther(); }}
                aria-label="How did you hear about us — other"
                maxLength={150}
              />
              <button
                type="button"
                onClick={submitHowHeardOther}
                disabled={!howHeardOtherText.trim()}
              >
                Submit
              </button>
            </div>
          )}
          {howHeardSaved && <p className="qp-confirmation-howheard-thanks">Thanks!</p>}
        </div>

        <div className="qp-confirmation-contact">
          <p>Questions? Call us at <a href={`tel:${config.phone}`}>{config.phone}</a></p>
          <p className="qp-confirmation-hours">Available 8am to 5pm</p>
        </div>
      </div>
    );
  }

  const neighbourCount = generateNeighbourCount(quote.zipCode, 10, 60);
  const hasOneTime = quote.oneTimeServices && quote.oneTimeServices.length > 0;
  const oneTimeTotal = hasOneTime
    ? quote.oneTimeServices.reduce((sum, s) => sum + s.price, 0)
    : 0;

  // Net 30 mode (driven by the `net-30` tag on the Sell deal, backfilled
  // into quote.paymentTerms by api/quote/[dealId].js). When set, the
  // PaymentForm is skipped (no card collected) and the agreement uses
  // the Net 30 clauses + consent text. Only meaningful for one-time
  // deals; monthly service is autopay-only.
  const isNet30 = !effectiveHasMonthly && quote.paymentTerms === 'net_30';

  // Pay-by-check mode (driven by the `pay-by-check` tag on the Sell deal,
  // backfilled into quote.paymentTerms by api/quote/[dealId].js). Same shape
  // as Net 30: the PaymentForm is skipped, no card or bank account is
  // collected, and the agreement uses the check clause set + consent text.
  // The mirror image of isNet30 in scope: Net 30 is one-time-only, this is
  // monthly-only, so the two can never both be true and no other deal shape
  // changes behaviour. A one-time deal tagged pay-by-check is a deliberate
  // no-op (`net-30` is the one-time invoice mechanism).
  const isCheck = effectiveHasMonthly && quote.paymentTerms === 'check';

  // Service tier list (CRO 22): use serviceIncludesByTier when available, otherwise legacy serviceIncludes
  const tier = quote.serviceTier || 'full';
  const tierIncludes = (config.serviceIncludesByTier && config.serviceIncludesByTier[tier])
    || config.serviceIncludes;
  const tierLabelBase = (config.serviceTierLabels && config.serviceTierLabels[tier]) || 'Weekly Service';
  // Scope label: spa_only tier means the recurring service IS the spa
  // (no pool component), so hasMonthlyService alone is not a signal of
  // pool service. Read the tier first; otherwise fall back to the
  // pool/one-time/spa-flag combination for legacy quotes.
  const scopeLabel = (() => {
    if (isSpaOnly) return 'Spa only';
    const hasPool = effectiveHasMonthly || (quote.oneTimeServices && quote.oneTimeServices.length);
    if (quote.hasSpa && hasPool) return 'Pool & Spa';
    if (quote.hasSpa) return 'Spa only';
    return 'Pool only';
  })();
  const tierLabel = isAlternateWeeks
    ? `${tierLabelBase.replace(/^Weekly\s+/i, '')}, every other week (${scopeLabel})`
    : isTwiceWeekly
    ? `${tierLabelBase.replace(/^Weekly\s+/i, '')}, twice weekly (${scopeLabel})`
    : `${tierLabelBase} (${scopeLabel})`;
  const timeline = effectiveHasMonthly
    ? config.timeline.map((step) => quote.seasonalPricing?.startWindow && /first|start/i.test(step.title)
      ? { ...step, title: 'First visit', description: `Service begins ${quote.seasonalPricing.startWindow}; exact date to be confirmed` }
      : isAlternateWeeks && step.title === 'Weekly Visits'
      ? { ...step, title: 'Every-other-week visits', description: 'Winter visits every other week; weekly visits in summer' }
      : isTwiceWeekly && step.title === 'Weekly Visits'
      ? { ...step, title: 'Twice-weekly Visits', description: 'Same days each week, consistent technician' }
      : step)
    : (config.oneTimeTimeline || config.timeline);

  // Prefer the structured service-address fields the publisher emits.
  // Falls back to parsing quote.address for legacy KV entries (30-day
  // TTL) that pre-date the publisher change. See deal 199482040.
  const serviceAddress = normalizeServiceAddress(quote.serviceAddressFields)
    || parseAddress(quote.address);

  const scrollToSubmit = () => {
    if (submitSectionRef.current) {
      submitSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className={`qp-page ${effectiveHasMonthly && stickyVisible ? 'has-sticky-cta' : ''}`}>

      {/* ========== 1. HERO (gradient, matching PDFMonkey cover) ========== */}
      <section className="qp-hero">
        <img src={config.logo} alt={config.name} className="qp-hero-logo" />
        <div className="qp-hero-eyebrow">{isSpaOnly ? 'Spa Service Proposal' : 'Pool Service Proposal'}</div>
        <p className="qp-hero-greeting">Prepared for {quote.contactName}</p>
        <h1 className="qp-hero-title">{isSpaOnly ? 'Spa service,' : 'Pool service,'}<br />made easy.</h1>
        <p className="qp-hero-subtitle">{isSpaOnly ? 'Clean, balanced water, guaranteed.' : 'Crystal clear water, guaranteed.'}</p>
        <p className="qp-hero-address">{quote.address}</p>
        {!heroImageFailed && (
          <div className="qp-hero-image">
            {/* Served from this app's own public/ so it works on every brand
                domain. It used to point at cabanapools.com/wp-content, which
                started returning 403 once that site moved off WordPress. */}
            <img
              src="/images/green-to-clean.webp"
              alt="Green to clean pool transformation"
              width="640"
              height="360"
              fetchpriority="high"
              onError={() => setHeroImageFailed(true)}
            />
          </div>
        )}

        {/* Trust bar (inside hero for continuous gradient bg) */}
        <div className="qp-trust-bar">
          <div className="qp-trust-stat">
            <div className="qp-trust-number">{config.socialProofTotal}</div>
            <div className="qp-trust-label">Pools Served</div>
          </div>
          <div className="qp-trust-divider" />
          <div className="qp-trust-stat">
            <div className="qp-trust-number qp-trust-star">
              4.9 <StarSvg />
            </div>
            <div className="qp-trust-label">Google Rating</div>
          </div>
          <div className="qp-trust-divider" />
          <div className="qp-trust-stat">
            <div className="qp-trust-number">CPO</div>
            <div className="qp-trust-label">Certified Pros</div>
          </div>
          <div className="qp-trust-divider" />
          <div className="qp-trust-stat">
            <div className="qp-trust-number">100%</div>
            <div className="qp-trust-label">Guarantee</div>
          </div>
        </div>
      </section>

      {/* ========== PROMO BANNER (only when the deal has a promo stamped) ========== */}
      {(() => {
        const promoCfg = getPromo(quote.promo);
        if (!promoCfg) return null;
        return (
          <section className="qp-promo-banner" role="status">
            <div className="qp-promo-tag">{promoCfg.bannerHeading}</div>
            <div className="qp-promo-text">{promoCfg.bannerSubtext}</div>
          </section>
        );
      })()}

      {/* ========== 2. PRICING CARD (weekly-led) ========== */}
      {effectiveHasMonthly && (
        <Reveal type="scale" section="pricing">
          <section className="qp-pricing-section">
            <div className="qp-pricing-card" ref={pricingCardRef}>
              <div className="qp-pricing-header">
                <div className="qp-pricing-label">{tierLabel}</div>
                <div className="qp-pricing-weekly">
                  ${fmtMoney(perVisitSeasonal ? effectiveMonthlyPrice / (4 * visitsPerWeek) : effectiveWeeklyPrice)}<span className="qp-pricing-weekly-unit">{perVisitSeasonal ? "/visit" : "/week"}</span>
                </div>
                <div className="qp-pricing-monthly-secondary">
                  Billed monthly: <strong>${fmtMoney(effectiveMonthlyPrice)}/mo</strong>
                  {quote.seasonalPricing && ` (${quote.activeSeason} rate)`}
                </div>
                {quote.seasonalPricing && (
                  <div className="qp-pricing-seasons">
                    {quote.seasonalPricing.startWindow && <p>Service begins {quote.seasonalPricing.startWindow}. We will confirm the first visit date with you.</p>}
                    {seasonalSummaryLines(quote.seasonalPricing).map((line) => (
                      <div key={line} className="qp-pricing-season-row">{line}</div>
                    ))}
                  </div>
                )}
                <div className="qp-cancel-line">
                  <IoCloseCircle size={12} aria-hidden="true" />
                  <span>{config.cancelAnytimeText || 'Cancel anytime, no contract.'}</span>
                </div>
              </div>
              <div className="qp-pricing-body">
                <span className="qp-pricing-tag"><CheckSvg /> {cadenceLabel} visits</span>
                <span className="qp-pricing-tag"><CheckSvg /> Essential chemicals</span>
                <span className="qp-pricing-tag"><CheckSvg /> Photo reports</span>
                <span className="qp-pricing-tag"><CheckSvg /> 7-day support</span>
              </div>
              {hasOneTime && (
                <div className="qp-pricing-onetime">
                  <div className="qp-pricing-onetime-heading">Plus one-time charges (billed at time of service)</div>
                  {quote.oneTimeServices.map((s, i) => (
                    <div key={i} className="qp-pricing-onetime-row">
                      <span>{s.name}</span>
                      <strong>${fmtMoney(s.price)}</strong>
                    </div>
                  ))}
                  {quote.oneTimeServices.length > 1 && (
                    <div className="qp-pricing-onetime-row qp-pricing-onetime-total">
                      <span>One-time total</span>
                      <strong>${fmtMoney(quote.oneTimeServices.reduce((s, x) => s + Number(x.price || 0), 0))}</strong>
                    </div>
                  )}
                </div>
              )}
            </div>
          </section>
        </Reveal>
      )}

      {/* ========== QUOTE VALIDITY ========== */}
      {effectiveHasMonthly && (
        <div className="qp-quote-expiry">
          <IoLockClosed size={13} aria-hidden="true" />
          <span>{quote.expiresAt
            ? `This quote is valid until ${new Date(quote.expiresAt).toLocaleDateString()}. Pricing may change after that.`
            : 'Please review this quote before signing. Pricing may change after it expires.'}</span>
        </div>
      )}

      {/* ========== 3. WHAT'S INCLUDED ========== */}
      {effectiveHasMonthly && (
        <Reveal section="whats_included">
          <section className="qp-included-section">
            <h2 className="qp-section-title">Every Visit Includes</h2>
            <p className="qp-section-subtitle">
              {tierLabel}: {cadenceLabel.toLowerCase()} {isSpaOnly ? 'spa' : 'pool'} care with photo reports after every visit.
            </p>
            <Reveal type="stagger">
              <div className="qp-service-grid">
                {tierIncludes.map((item, i) => (
                  <div key={i} className="qp-service-item">
                    <span className="qp-check-icon" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </Reveal>
            <p className="qp-chemicals-note">{isSpaOnly && config.spaChemicalsNote ? config.spaChemicalsNote : config.chemicalsNote}</p>
            {quote.hasSaltSystem && (
              <p className="qp-salt-note">{config.saltSystemNote}</p>
            )}
          </section>
        </Reveal>
      )}

      {/* ========== HIGHLIGHT BOX ========== */}
      {effectiveHasMonthly && (
        <Reveal type="left" section="highlight">
          <div className="qp-highlight-box">
            <div className="qp-highlight-title"><span aria-hidden="true">&#128247;</span> We Don't Just Say We Cleaned. We Prove It.</div>
            <div className="qp-highlight-text">
              After every visit, you'll get an email with photos of your {isSpaOnly ? 'spa' : 'sparkling pool'} and notes from your technician. Never wonder if {isSpaOnly ? 'the tech' : 'the pool guy'} showed up again.
            </div>
          </div>
        </Reveal>
      )}

      {/* ========== 5. ONE-TIME SERVICES TABLE ========== */}
      {hasOneTime && (() => {
        const promoCfg = getPromo(quote.promo);
        // Invoice-total promos (e.g. service200comp) apply to the
        // one-time charges shown here. Render the credit line + amount
        // due so the customer sees the discounted total before signing
        // instead of finding it on the post-sign agreement page.
        const showPromo = promoCfg && promoCfg.appliesTo === 'invoice_total';
        const promoBase = oneTimeTotal + (effectiveHasMonthly ? Number(effectiveMonthlyPrice || 0) : 0);
        const promoApplied = showPromo ? Math.min(promoCfg.amount, promoBase) : 0;
        const dueToday = showPromo ? Math.max(0, promoBase - promoCfg.amount) : oneTimeTotal;
        return (
        <Reveal type="scale" section="one_time_services">
          <section className="qp-onetime-section">
            <h2 className="qp-section-title">One-Time Services</h2>
            <div className="qp-onetime-table">
              <div className="qp-onetime-header">
                <span>Service</span>
                <span>Price</span>
              </div>
              {quote.oneTimeServices.map((item, i) => (
                <div key={i} className="qp-onetime-row">
                  <span className="qp-onetime-name">{item.name}</span>
                  <span className="qp-onetime-price">${fmtMoney(item.price)}</span>
                </div>
              ))}
              <div className="qp-onetime-total">
                <span className="qp-onetime-total-label">Total One-Time Charges</span>
                <span className="qp-onetime-total-price">${fmtMoney(oneTimeTotal)}</span>
              </div>
              {showPromo && promoApplied > 0 && (
                <>
                  <div className="qp-onetime-row qp-onetime-promo">
                    <span className="qp-onetime-name">{promoCfg.agreementLabel}</span>
                    <span className="qp-onetime-price">-${fmtMoney(promoApplied)}</span>
                  </div>
                  <div className="qp-onetime-total qp-onetime-due">
                    <span className="qp-onetime-total-label"><strong>Amount due today</strong></span>
                    <span className="qp-onetime-total-price"><strong>${fmtMoney(dueToday)}</strong></span>
                  </div>
                </>
              )}
            </div>
          </section>
        </Reveal>
        );
      })()}

      {/* ========== 5b. RECURRING MONTHLY HIGHLIGHT (when monthly + one-time both present) ========== */}
      {effectiveHasMonthly && hasOneTime && (
        <Reveal section="combined_totals">
          <div className="qp-totals-card" aria-label="Recurring monthly service">
            <div className="qp-totals-row">
              <span className="qp-totals-label">Recurring monthly service</span>
              <span className="qp-totals-amount">${fmtMoney(effectiveMonthlyPrice)}<span>/mo</span></span>
            </div>
            <div className="qp-totals-note">
              One-time charges are billed at the time of service. Your monthly service is invoiced on the 1st (first month prorated).
            </div>
          </div>
        </Reveal>
      )}

      {/* ========== 6. WHAT HAPPENS NEXT ========== */}
      <Reveal type="right" section="timeline">
        <section className="qp-timeline">
          <h2 className="qp-section-title">What Happens Next</h2>
          <div className="qp-timeline-steps">
            {timeline.map((step, i) => (
              <div key={i} className="qp-timeline-step">
                <div className="qp-timeline-number">{step.step}</div>
                <div className="qp-timeline-text">
                  <strong>{step.title}:</strong> {showStartPromise && step.step === '2' ? SERVICE_START_FALLBACK : step.description}
                </div>
              </div>
            ))}
          </div>
        </section>
      </Reveal>

      {/* ========== 7. SOCIAL PROOF ========== */}
      <Reveal section="social_proof">
        <section className="qp-social-proof">
          <h2 className="qp-section-title">What Our Customers Say</h2>
          <div className="qp-reviews">
            {((!effectiveHasMonthly && Array.isArray(config.oneTimeReviews) && config.oneTimeReviews.length > 0)
              ? config.oneTimeReviews
              : config.reviews
            ).map((review, i) => (
              <div key={i} className="qp-review-card">
                <div className="qp-review-stars" aria-label={`${review.stars} out of 5 stars`}>
                  {Array.from({ length: review.stars }, (_, j) => <StarSvg key={j} />)}
                </div>
                <p className="qp-review-text">"{review.text}"</p>
                <div className="qp-review-author">
                  <strong>{review.name}</strong> <span>{review.location}</span>
                </div>
              </div>
            ))}
          </div>
          <p className="qp-neighbors">
            Join <strong>{neighbourCount} neighbors</strong> in {quote.zipCode} who trust {config.name}
          </p>
          <p className="qp-total-pools">
            {config.socialProofTotal} {config.socialProofText}
          </p>
        </section>
      </Reveal>

      {askWaterSetup && (
        <section className="qp-access-info" id="qp-water-setup">
          <div className="qp-form-section-label">Your Pool &amp; Spa</div>
          <div className="qp-field-group">
            <div className="qp-field-label" id="qp-water-setup-label">Which best describes what we'll be servicing?</div>
            <div
              className={`qp-radio-segment qp-radio-stack ${waterSetupError && !waterSetup ? 'qp-radio-error' : ''}`}
              role="radiogroup"
              aria-labelledby="qp-water-setup-label"
              aria-required="true"
            >
              {WATER_SETUP_OPTIONS.map((option) => (
                <label key={option.value}>
                  <input
                    type="radio"
                    name="waterSetup"
                    value={option.value}
                    checked={waterSetup === option.value}
                    onChange={(e) => { setWaterSetup(e.target.value); setWaterSetupError(false); }}
                  />
                  {option.label}
                </label>
              ))}
            </div>
            {waterSetupError && !waterSetup && (
              <p className="qp-field-error" role="alert">Please choose your pool and spa setup.</p>
            )}
          </div>
        </section>
      )}

      {/* ========== 8. ACCESS & PROPERTY INFO (now ABOVE payment) ========== */}
      <section className="qp-access-info">
        <div className="qp-form-section-label">Access &amp; Property Info <span className="qp-optional">(optional)</span></div>
        <div className="qp-field-group">
          <div className="qp-input-row qp-access-row-stack">
            <div className="qp-input-col">
              <div className="qp-field-label">Gate Code</div>
              <input
                className="qp-input"
                name="gateCode"
                placeholder="Gate code"
                value={accessInfo.gateCode}
                onChange={handleAccessChange}
              />
            </div>
            <div className="qp-input-col">
              <div className="qp-field-label">Key Needed?</div>
              <div
                className="qp-radio-segment"
                role="radiogroup"
                aria-label="Key needed"
              >
                <label>
                  <input
                    type="radio"
                    name="needsKey"
                    value="yes"
                    checked={accessInfo.needsKey === 'yes'}
                    onChange={handleAccessChange}
                  />
                  Yes
                </label>
                <label>
                  <input
                    type="radio"
                    name="needsKey"
                    value="no"
                    checked={accessInfo.needsKey === 'no'}
                    onChange={handleAccessChange}
                  />
                  No
                </label>
              </div>
            </div>
          </div>
          <div className="qp-field-label">Special Access Notes</div>
          <textarea
            className="qp-input qp-textarea"
            name="notes"
            placeholder="Any special instructions"
            rows={3}
            value={accessInfo.notes}
            onChange={handleAccessChange}
          />
        </div>
      </section>

      {/* ========== 9. PAYMENT INFO (no Reveal; stable presentation) ========== */}
      {(effectiveHasMonthly || hasOneTime) && !isNet30 && !isCheck && (
        <PaymentForm ref={paymentFormRef} brandConfig={config} serviceAddress={serviceAddress} customerPhone={quote.contactPhone} customerEmail={quote.contactEmail} isMonthly={effectiveHasMonthly} monthlyPrice={effectiveMonthlyPrice} oneTimeTotal={oneTimeTotal} />
      )}
      {isNet30 && (
        <section className="qp-payment-section" aria-labelledby="qp-net30-heading">
          <h2 id="qp-net30-heading" className="qp-section-title">Payment terms</h2>
          <p className="qp-net30-terms">
            Net 30. We'll invoice you for the services in this proposal at
            completion. Payment is due within 30 days of the invoice date.
            No card or bank account on file.
          </p>
        </section>
      )}
      {isCheck && (
        <section className="qp-payment-section" aria-labelledby="qp-check-heading">
          <h2 id="qp-check-heading" className="qp-section-title">Payment terms</h2>
          <p className="qp-net30-terms">
            Payable by check. We'll email you an invoice on the 1st of each
            month for that month's service. No credit card or bank account is
            required and nothing is charged automatically.
          </p>
        </section>
      )}

      {/* ========== 10. SERVICE AGREEMENT (visible cards + clickwrap consent) ========== */}
      {(effectiveHasMonthly || hasOneTime) && (quote.agreementContent || config.serviceAgreement) && (
        <section className="qp-agreement-section" aria-labelledby="qp-agreement-heading">
          <h2 id="qp-agreement-heading" className="qp-section-title">Your service agreement</h2>
          <p className="qp-agreement-intro">{quote.agreementContent?.intro || config.serviceAgreement.intro}</p>
          <div className="qp-agreement-grid">
            {(quote.agreementContent?.cards || (effectiveHasMonthly
              ? getMonthlyAgreementCards(isCheck
                  ? (config.serviceAgreement.monthlyCheckCards || config.serviceAgreement.cards)
                  : config.serviceAgreement.cards, visitsPerWeek, quote.seasonalPricing)
              : isNet30
                ? (config.serviceAgreement.oneTimeNet30Cards
                    || config.serviceAgreement.oneTimeCards
                    || config.serviceAgreement.cards)
                : (config.serviceAgreement.oneTimeCards || config.serviceAgreement.cards)
            ))
              // Cards may opt out of specific tiers via excludeForTiers.
              // Used by the weather-conditions clause to drop itself
              // from chem-only and chem-and-brush agreements.
              .filter((card) => !(Array.isArray(card.excludeForTiers) && card.excludeForTiers.includes(tier)))
              .map((card, i) => {
              // Specialty-treatments card uses live data from /api/specialty-extras
              // when available, falling back to the static brand.js addons.
              const isSpecialtyCard = Array.isArray(card.addons) && card.addons.length > 0;
              const hasPoolForExtras = !isSpaOnly && (!!quote.hasMonthlyService
                || (Array.isArray(quote.oneTimeServices) && quote.oneTimeServices.length > 0));
              const isSpaOnlyForExtras = isSpaOnly || (!!quote.hasSpa && !hasPoolForExtras);
              const dynamicExtras = (isSpecialtyCard && Array.isArray(specialtyExtras))
                ? specialtyExtras
                    .filter((e) => {
                      if (e.notBilled) return false;
                      // Annual heater tune-up moves to an onboarding upsell email,
                      // so it shouldn't appear in the in-quote specialty list.
                      if (e.id === 'annual-heater-tuneup') return false;
                      const price = Number(e.price);
                      const hasPrice = (e.priceLabel && String(e.priceLabel).trim().length > 0)
                        || (Number.isFinite(price) && price > 0);
                      if (!hasPrice) return false;
                      const cond = (e.conditional || '').toString().toLowerCase();
                      if (!cond) return true;
                      if (/salt/.test(cond)) return !!quote.hasSaltSystem;
                      // Mixed pool + standalone-spa deals keep
                      // spa-only-conditional extras (Spa Drain) even
                      // though the tier is full. Mirrors
                      // resolveSpecialtyExtras in _lib/specialty-extras.
                      if (/spa\s*only/.test(cond)) return isSpaOnlyForExtras || quoteHasStandaloneSpa(quote);
                      if (/spa/.test(cond)) return !!quote.hasSpa;
                      return true;
                    })
                    .map((e) => {
                      const override = overriddenExtraPrice(e, quote);
                      return {
                        name: e.name,
                        frequency: e.frequency,
                        priceLabel: override !== null
                          ? `$${fmtMoney(override)}`
                          : (e.priceLabel || (Number.isFinite(Number(e.price)) && Number(e.price) > 0 ? `$${fmtMoney(e.price)}` : '')),
                      };
                    })
                : null;
              const fallbackAddons = isSpecialtyCard
                ? card.addons
                    .filter((a) => !a.requires
                      || (a.requires === 'salt' && quote.hasSaltSystem)
                      || (a.requires === 'spa' && quote.hasSpa)
                      || (a.requires === 'spa-only' && isSpaOnlyForExtras))
                    .map((a) => ({ name: a.name, frequency: a.frequency, priceLabel: '' }))
                : [];
              const renderedAddons = dynamicExtras || fallbackAddons;
              const showAddons = isSpecialtyCard && renderedAddons.length > 0;
              const hasDetails = !!card.details;
              const isOpen = !!openAgreementCards[i];
              const wide = showAddons;
              return (
                <div
                  key={i}
                  className={`qp-agreement-card ${wide ? 'qp-agreement-card-wide' : ''}`}
                >
                  <div className="qp-agreement-card-header">
                    <div className="qp-agreement-card-title">{card.title}</div>
                    {hasDetails && (
                      <button
                        type="button"
                        className="qp-agreement-card-toggle"
                        onClick={() => {
                          setOpenAgreementCards((prev) => ({ ...prev, [i]: !prev[i] }));
                          if (!isOpen) {
                            trackEvent(EVENTS.QUOTE_AGREEMENT_CARD_OPENED, {
                              card_index: i,
                              card_title: String(card.title || '').slice(0, 80),
                            });
                          }
                        }}
                        aria-expanded={isOpen}
                        aria-label={isOpen ? `Hide details for ${card.title}` : `More info on ${card.title}`}
                      >
                        {isOpen ? <IoRemove size={16} /> : <IoAdd size={16} />}
                      </button>
                    )}
                  </div>
                  <div className="qp-agreement-card-body">{card.body}</div>
                  {hasDetails && isOpen && (
                    <div className="qp-agreement-card-details" id={`qp-agreement-details-${i}`}>
                      {card.details}
                    </div>
                  )}
                  {showAddons && (
                    <div className="qp-agreement-addons">
                      {renderedAddons.map((a, j) => (
                        <div key={j} className="qp-agreement-addon">
                          <span className="qp-agreement-addon-name">{a.name}</span>
                          <span className="qp-agreement-addon-freq">{a.frequency}</span>
                          {a.priceLabel
                            ? <span className="qp-agreement-addon-price">{a.priceLabel}</span>
                            : <span className="qp-agreement-addon-price" />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="qp-consent-block">
            <label className={`qp-consent-row ${agreementError && !agreementChecked ? 'qp-consent-error' : ''}`} htmlFor="qp-consent-checkbox">
              <input
                id="qp-consent-checkbox"
                type="checkbox"
                checked={agreementChecked}
                onChange={(e) => {
                  setAgreementChecked(e.target.checked);
                  if (e.target.checked) setAgreementError(false);
                  trackEvent(
                    e.target.checked ? EVENTS.QUOTE_CONSENT_CHECKED : EVENTS.QUOTE_CONSENT_UNCHECKED,
                    { deal_id: dealId }
                  );
                }}
              />
              <span>{quote.agreementContent?.consent || (effectiveHasMonthly
                ? (isCheck
                    ? (config.serviceAgreement.monthlyCheckConsentText
                        || config.serviceAgreement.consentText)
                    : config.serviceAgreement.consentText)
                : isNet30
                  ? (config.serviceAgreement.oneTimeNet30ConsentText
                      || config.serviceAgreement.oneTimeConsentText
                      || config.serviceAgreement.consentText)
                  : (config.serviceAgreement.oneTimeConsentText || config.serviceAgreement.consentText))}</span>
            </label>
            {agreementError && !agreementChecked && (
              <div className="qp-consent-error-msg">Please check the box above to continue.</div>
            )}
            <p className="qp-esign-disclosure">{quote.agreementContent?.disclosure || (effectiveHasMonthly
              ? config.serviceAgreement.esignDisclosure
              : isNet30
                ? (config.serviceAgreement.oneTimeNet30EsignDisclosure
                    || config.serviceAgreement.oneTimeEsignDisclosure
                    || config.serviceAgreement.esignDisclosure)
                : (config.serviceAgreement.oneTimeEsignDisclosure || config.serviceAgreement.esignDisclosure))}</p>
          </div>
        </section>
      )}

      {/* ========== 11. SUBMIT ========== */}
      <Reveal type="scale" section="submit">
        <section className="qp-signature-section" id="qp-submit" ref={submitSectionRef}>
          {!effectiveHasMonthly && (
            <p className="qp-agree-text">
              By proceeding, I agree to the terms described in this proposal.
            </p>
          )}
          {effectiveHasMonthly && (
            <div className="qp-cancel-line" style={{ marginBottom: 16, background: '#f0fdf4', color: '#166534' }}>
              <IoCloseCircle size={12} aria-hidden="true" />
              <span>{config.cancelAnytimeText || 'Cancel anytime, no contract.'}</span>
            </div>
          )}
          {submitError && (
            <div className="qp-submit-error" role="alert">{submitError}</div>
          )}
          {reviewRequired && (
            <button type="button" className="qp-submit-btn" onClick={() => window.location.reload()}>
              Reload and review quote
            </button>
          )}
          <button
            className="qp-submit-btn"
            onClick={handleSubmit}
            disabled={submitting || reviewRequired}
          >
            {submitting
              ? (<><span className="qp-submit-spinner" aria-hidden="true" /> Processing...</>)
              : effectiveHasMonthly
                ? ctaCopyMonthly
                : ctaCopyOnetime}
          </button>
          {submitting && (
            <div className="qp-submit-loading" role="status" aria-live="polite">
              {(isNet30 || isCheck)
                ? "Saving your service agreement. This usually takes 5-10 seconds. Please don't close this tab."
                : "Saving your service agreement and storing your payment method on file. This usually takes 5-10 seconds. Please don't close this tab."}
            </div>
          )}
          <div className="qp-security-badge" style={{ marginTop: 16 }}>
            <IoLockClosed size={14} aria-hidden="true" />
            <span>{isCheck
              ? 'Invoiced monthly through QuickBooks Online. No card or bank account on file.'
              : isNet30
                ? 'Invoiced through QuickBooks Online after completion. Payment due within 30 days of invoice.'
                : 'Billed directly through QuickBooks Online, the same system trusted by 7M+ businesses.'}</span>
          </div>
        </section>
      </Reveal>

      {/* ========== FOOTER ========== */}
      <footer className="qp-footer">
        <p>Questions? Call <a href={`tel:${config.phone}`}>{config.phone}</a></p>
        <p>Cabana Pools | Lic #1133031 | Bonded &amp; $2M Insured</p>
        <p className="qp-footer-license">&copy; {new Date().getFullYear()} {config.name}. All rights reserved.</p>
      </footer>

      {/* ========== STICKY CTA ========== */}
      {effectiveHasMonthly && (
        <div className={`qp-sticky-cta ${stickyVisible ? 'visible' : ''}`} role="region" aria-label="Quick start">
          <div className="qp-sticky-cta-info">
            <div className="qp-sticky-cta-price">${fmtMoney(perVisitSeasonal ? effectiveMonthlyPrice / (4 * visitsPerWeek) : effectiveWeeklyPrice)}/{perVisitSeasonal ? "visit" : "week"} &middot; ${fmtMoney(effectiveMonthlyPrice)}/mo</div>
            <div className="qp-sticky-cta-sub">{config.cancelAnytimeText || 'Cancel anytime, no contract.'}</div>
          </div>
          <button className="qp-sticky-cta-btn" onClick={scrollToSubmit}>
            {ctaCopyMonthly}
          </button>
        </div>
      )}
    </div>
  );
};

export default QuotePage;
